// src/components/layouts/app/sub/SearchBar.tsx
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ChevronRight } from "lucide-react";

interface SearchBarProps {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  handleSearch: (e: React.FormEvent) => void;
  placeholder?: string;
  scrolled?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
  searchQuery,
  setSearchQuery,
  handleSearch,
  placeholder = "Rechercher...",
  scrolled = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Suggestions simple
  const suggestions = ["iPhone", "Samsung", "Casque audio", "Ordinateur", "Smartwatch"];

  // Fermeture au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filtrage dynamique
  const filteredSuggestions = searchQuery.trim()
    ? suggestions.filter((s) => s.toLowerCase().includes(searchQuery.toLowerCase()))
    : suggestions;

  const handleSuggestionClick = (text: string) => {
    setSearchQuery(text);
    setShowSuggestions(false);
    inputRef.current?.blur();
    handleSearch({ preventDefault: () => {} } as React.FormEvent);
  };

  const clearInput = () => {
    setSearchQuery("");
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  return (
    <div className="relative w-full z-50" ref={containerRef}>
      <form
        onSubmit={(e) => {
          handleSearch(e);
          setShowSuggestions(false);
        }}
        className={`w-full ${scrolled ? "max-w-md" : "max-w-lg"}`}
      >
        <div
          className={`relative flex items-center rounded-lg border transition-all duration-300 ${
            isFocused
              ? "ring-2 ring-primary border-primary/30 shadow-md bg-white"
              : "border-gray-200 hover:border-gray-300 bg-white"
          }`}
        >
          <Search className="absolute left-3 w-4 h-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              setIsFocused(true);
              setShowSuggestions(true);
            }}
            onBlur={() => setTimeout(() => setIsFocused(false), 150)}
            placeholder={placeholder}
            className="w-full pl-9 pr-14 py-2 bg-transparent text-gray-800 rounded-lg focus:outline-none"
          />
          <div className="absolute right-1 flex items-center gap-1">
            {searchQuery && (
              <button type="button" onClick={clearInput} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-3.5 h-3.5 text-gray-500" />
              </button>
            )}
            <button
              type="submit"
              className="px-3 py-1 bg-primary text-white text-sm font-medium rounded-md hover:opacity-90 transition"
            >
              Go
            </button>
          </div>
        </div>
      </form>

      <AnimatePresence>
        {showSuggestions && filteredSuggestions.length > 0 && (
          <>
            {/* Overlay léger */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/10 z-40"
              onClick={() => setShowSuggestions(false)}
            />
            {/* Panneau suggestions */}
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="absolute top-full left-0 right-0 mt-2 bg-white border rounded-xl shadow-xl z-50 overflow-hidden"
            >
              <div className="py-2">
                {filteredSuggestions.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(s)}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-primary/5 flex justify-between items-center group"
                  >
                    <span className="text-gray-700 group-hover:text-primary">{s}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-primary group-hover:translate-x-0.5 transition" />
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchBar;